import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { del, get, logout, post, put, upgradeGuest, useAuth } from '../api';
import { z } from 'zod';
import { passwordSchema } from '../validation';

interface Profile {
  id: string;
  username: string;
  email: string | null;
  emailVerified: string | null;
  hasPassword: boolean;
  isGuest: boolean;
}

const emailSchema = z.string().email('Invalid email address');

export default function AccountPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>(
    'idle'
  );

  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [upgradeUsername, setUpgradeUsername] = useState('');
  const [upgradeEmail, setUpgradeEmail] = useState('');
  const [upgradePassword, setUpgradePassword] = useState('');
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (auth === null) {
      navigate('/login', { replace: true });
      return;
    }
    get<Profile>('auth/me')
      .then((p) => {
        setProfile(p);
        setUpgradeUsername(p.username);
      })
      .finally(() => setLoading(false));
  }, [auth, navigate]);

  const handleResendVerification = async () => {
    setResendStatus('sending');
    try {
      await post('auth/resend-verification', {});
      setResendStatus('sent');
    } catch {
      setResendStatus('idle');
    }
  };

  const handleChangeEmail = async (event: FormEvent) => {
    event.preventDefault();
    setEmailError(null);
    setEmailSuccess(false);

    const result = emailSchema.safeParse(newEmail);
    if (!result.success) {
      setEmailError(result.error.issues[0].message);
      return;
    }

    setEmailLoading(true);
    try {
      await put('auth/email', { email: newEmail, password: emailPassword });
      setProfile((prev) => (prev ? { ...prev, email: newEmail } : prev));
      setNewEmail('');
      setEmailPassword('');
      setEmailSuccess(true);
    } catch (e: any) {
      setEmailError(e?.response?.data?.message || 'Could not update email.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    const result = passwordSchema.safeParse(newPassword);
    if (!result.success) {
      setPasswordError(result.error.issues[0].message);
      return;
    }

    setPasswordLoading(true);
    try {
      await post('auth/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setPasswordSuccess(true);
    } catch (e: any) {
      setPasswordError(
        e?.response?.data?.message || 'Could not update password.'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpgrade = async (event: FormEvent) => {
    event.preventDefault();
    setUpgradeError(null);

    const emailResult = emailSchema.safeParse(upgradeEmail);
    if (!emailResult.success) {
      setUpgradeError(emailResult.error.issues[0].message);
      return;
    }
    const passwordResult = passwordSchema.safeParse(upgradePassword);
    if (!passwordResult.success) {
      setUpgradeError(passwordResult.error.issues[0].message);
      return;
    }

    setUpgradeLoading(true);
    try {
      const result = await upgradeGuest(
        upgradeUsername,
        upgradeEmail,
        upgradePassword
      );
      if (result.ok) {
        setProfile((prev) =>
          prev
            ? {
                ...prev,
                username: upgradeUsername,
                email: upgradeEmail,
                isGuest: false,
                hasPassword: true,
              }
            : prev
        );
        setUpgradePassword('');
      } else {
        setUpgradeError(result.error ?? 'Could not upgrade account.');
      }
    } finally {
      setUpgradeLoading(false);
    }
  };

  const deleteEnabled = profile?.hasPassword
    ? deletePassword.length > 0
    : deleteConfirmText === 'DELETE';

  const handleDeleteAccount = async () => {
    setDeleteError(null);
    setDeleteLoading(true);
    try {
      await del(
        'auth/account',
        profile?.hasPassword ? { password: deletePassword } : undefined
      );
      await logout();
      navigate('/login', { replace: true });
    } catch (e: any) {
      setDeleteError(e?.response?.data?.message || 'Could not delete account.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  if (loading || !profile) {
    return <div className="has-text-centered">Loading...</div>;
  }

  return (
    <div>
      <div className="columns">
        <div className="column is-half is-offset-one-quarter">
          <h2 className="title is-3">My Account</h2>

          {!profile.isGuest && !profile.emailVerified && (
            <div className="notification is-warning">
              <p>Your email address is not verified.</p>
              <button
                className={`button is-small mt-2${
                  resendStatus === 'sending' ? ' is-loading' : ''
                }`}
                onClick={handleResendVerification}
                disabled={resendStatus !== 'idle'}
              >
                {resendStatus === 'sent'
                  ? 'Verification email sent'
                  : 'Resend verification email'}
              </button>
            </div>
          )}

          <div className="box">
            <p>
              <strong>Username:</strong> {profile.username}
            </p>
            <p>
              <strong>Email:</strong> {profile.email ?? 'None (guest account)'}
            </p>
            <button className="button is-light mt-3" onClick={handleLogout}>
              Log out
            </button>
          </div>

          {profile.isGuest ? (
            <div className="box">
              <h3 className="title is-5">Save your progress</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                You're playing as a guest. Add a username, email, and password
                to turn this into a full account so you never lose your
                progress.
              </p>
              <form onSubmit={handleUpgrade}>
                <div className="field">
                  <label className="label">Username</label>
                  <input
                    className="input"
                    type="text"
                    value={upgradeUsername}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setUpgradeUsername(e.target.value)
                    }
                  />
                </div>
                <div className="field">
                  <label className="label">Email</label>
                  <input
                    className={`input${upgradeError ? ' is-danger' : ''}`}
                    type="email"
                    value={upgradeEmail}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setUpgradeEmail(e.target.value)
                    }
                  />
                </div>
                <div className="field">
                  <label className="label">Password</label>
                  <input
                    className="input"
                    type="password"
                    value={upgradePassword}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setUpgradePassword(e.target.value)
                    }
                  />
                </div>
                {upgradeError && (
                  <p className="help is-danger">{upgradeError}</p>
                )}
                <button
                  className={`button is-primary${
                    upgradeLoading ? ' is-loading' : ''
                  }`}
                  type="submit"
                >
                  Save account
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className="box">
                <h3 className="title is-5">Change email</h3>
                <form onSubmit={handleChangeEmail}>
                  <div className="field">
                    <label className="label">New email</label>
                    <input
                      className={`input${emailError ? ' is-danger' : ''}`}
                      type="email"
                      value={newEmail}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewEmail(e.target.value)
                      }
                    />
                  </div>
                  <div className="field">
                    <label className="label">Current password</label>
                    <input
                      className="input"
                      type="password"
                      value={emailPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setEmailPassword(e.target.value)
                      }
                    />
                  </div>
                  {emailError && <p className="help is-danger">{emailError}</p>}
                  {emailSuccess && (
                    <p className="help is-success">Email updated.</p>
                  )}
                  <button
                    className={`button is-primary${
                      emailLoading ? ' is-loading' : ''
                    }`}
                    type="submit"
                  >
                    Update email
                  </button>
                </form>
              </div>

              <div className="box">
                <h3 className="title is-5">Change password</h3>
                <form onSubmit={handleChangePassword}>
                  <div className="field">
                    <label className="label">Current password</label>
                    <input
                      className="input"
                      type="password"
                      value={currentPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setCurrentPassword(e.target.value)
                      }
                    />
                  </div>
                  <div className="field">
                    <label className="label">New password</label>
                    <input
                      className={`input${passwordError ? ' is-danger' : ''}`}
                      type="password"
                      value={newPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setNewPassword(e.target.value)
                      }
                    />
                  </div>
                  {passwordError && (
                    <p className="help is-danger">{passwordError}</p>
                  )}
                  {passwordSuccess && (
                    <p className="help is-success">Password updated.</p>
                  )}
                  <button
                    className={`button is-primary${
                      passwordLoading ? ' is-loading' : ''
                    }`}
                    type="submit"
                  >
                    Update password
                  </button>
                </form>
              </div>
            </>
          )}

          <div className="box">
            <h3 className="title is-5">Delete account</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              This will permanently remove your ability to log in. Your game
              history will be kept but no longer attributed to you.
            </p>
            <button
              className="button is-danger is-light mt-3"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete account
            </button>
          </div>
        </div>
      </div>

      <div className={`modal ${showDeleteConfirm ? 'is-active' : ''}`}>
        <div
          className="modal-background"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowDeleteConfirm(false)}
        />
        <div
          style={{
            position: 'relative',
            backgroundColor: 'var(--panel-bg)',
            color: 'var(--text-primary)',
            border: '1px solid rgba(68,68,102,0.6)',
            borderRadius: 10,
            maxWidth: 400,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            margin: '0 16px',
          }}
        >
          <div
            style={{
              padding: '20px 24px 12px',
              borderBottom: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2
              className="title is-5 mb-0"
              style={{ color: 'var(--text-primary)' }}
            >
              Delete Account
            </h2>
            <button
              className="delete is-medium"
              aria-label="close"
              onClick={() => setShowDeleteConfirm(false)}
            />
          </div>
          <div style={{ padding: '20px 24px' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              This action cannot be undone. Your account will be deactivated and
              you will be logged out everywhere.
            </p>
            {profile.hasPassword ? (
              <div className="field mt-3">
                <label className="label">Confirm your password</label>
                <input
                  className="input"
                  type="password"
                  value={deletePassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDeletePassword(e.target.value)
                  }
                />
              </div>
            ) : (
              <div className="field mt-3">
                <label className="label">Type DELETE to confirm</label>
                <input
                  className="input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDeleteConfirmText(e.target.value)
                  }
                />
              </div>
            )}
            {deleteError && (
              <p className="help is-danger" style={{ marginTop: 12 }}>
                {deleteError}
              </p>
            )}
          </div>
          <div
            style={{
              padding: '12px 24px 20px',
              borderTop: '1px solid rgba(68,68,102,0.35)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              className="button is-light"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteError(null);
                setDeletePassword('');
                setDeleteConfirmText('');
              }}
            >
              Cancel
            </button>
            <button
              className={`button is-danger${
                deleteLoading ? ' is-loading' : ''
              }`}
              onClick={handleDeleteAccount}
              disabled={!deleteEnabled || deleteLoading}
            >
              Delete account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
